import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import ExportIcon from '@material-ui/icons/CloudDownload';
import FilterIcon from '@material-ui/icons/FilterList';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import DetailsIcon from '@material-ui/icons/Description';
import { DataTable } from '../data-table';
import { DynamicForm, FieldType } from '../dynamic-form';
import { AlertDialog } from '../alert-dialog';
import { ThreeBtn } from '../three-btn';
import { useRoutes, useApi, useAuth } from '../../hooks';
import { Report, ReportStatus } from '../../types';
import { useAlert } from '../../components/alert-info/use-alert';
import { setThemeData } from '../../store';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  topArea: {
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
  },
  separator: {
    flex: 1,
  },
  section: {
    maxWidth: '100%',
    marginTop: theme.spacing(3),
  },
  unread: {
    marginBottom: theme.spacing(2),
  },
}));

type DataListProps = {
  formPath: string;
  columns: any[];
  data?: any[];
  filterFields: { [key: string]: FieldType } | any;
};

type ReportData = {
  reports: Report[];
  maxPages: number;
  maxResultPage: number;
  pageNumber: number;
  sumUnreadReports: number;
};

export const DataList: React.FC<DataListProps> = ({
  formPath,
  columns,
  filterFields,
}: DataListProps) => {
  const { isAgent } = useAuth();
  const { getReport, exportReports, deleteReport } = useApi();
  const { navigate, setQueryParams, queryParams, currentPath } = useRoutes();
  const classes = useStyles();
  const [showFiltres, setShowFiltres] = useState(false);
  const filterInitialValues = useMemo(() => {
    return Object.keys(filterFields).reduce(
      (acc, key) => ({ ...acc, [key]: filterFields[key]?.type === 'text' ? '' : null }),
      {}
    );
  }, [filterFields]);
  const [data, setData] = useState<ReportData>();
  const [filtres, setFiltres] = useState();
  const { show, AlertTypes } = useAlert();
  const [open, setOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<number | string>();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();



  const toggleFiltres = useCallback(() => {
    setShowFiltres((show) => !show);
  }, [setShowFiltres]);

  const handleClickOpen = useCallback((reportId) => {
    setSelectedReport(reportId);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOnSearch = useCallback(
    (filtres) => {
      if (filtres) {
        const queryParams: any = Object.keys(filtres)
          .filter((key) => filtres[key])
          .reduce((acc, next) => ({ ...acc, [next]: filtres[next] }), {});
        setQueryParams(queryParams);
      }
    },
    [setQueryParams]
  );

  const handleOnSearchClick = useCallback(() => {
    handleOnSearch(filtres);
  }, [handleOnSearch, filtres]);

  const handleOnChange = useCallback((form) => {
    setFiltres(form.values);
  }, []);

  const handleOnPageChange = useCallback(
    (page) => {
      const currentFiltres = { ...filtres, pageNumber: page + 1 };
      handleOnSearch(currentFiltres);
    },
    [handleOnSearch, filtres]
  );

  const navigateToCreateAsct = navigate(`${currentPath?.path}/nouveau`);

  const handleOnClick = useCallback(
    (row) => {

      navigate(`${currentPath?.path}/${row.reportId}`)();
      localStorage.setItem('row', JSON.stringify({
        ...row, ...row.fieldReportsDto.products.reduce(
          (obj: any, item: any) => Object.assign(obj, {
            [item?.name]: item?.number
          }), {})
      }));
    },
    [navigate, currentPath]
  );

  const dataColumns = useMemo(
    () => [
      ...columns,
      {
        label: 'Actions',
        renderColumn: (row: Report) => (
          <div>
            <IconButton color="primary" onClick={() => handleOnClick(row.reportId)}>
              <DetailsIcon fontSize="small" />
            </IconButton>
            {row && row.reportStatus === 'Enregistré' && (
              <IconButton
                color="secondary"
                aria-label="delete"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClickOpen(row.reportId);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        ),
      },
    ],
    [columns, handleOnClick, handleClickOpen]
  );

  const getReportByTheme = useCallback(async () => {
    try {
      if (currentPath && currentPath.themeShortCode) {
        setLoading(true);
        const { themeShortCode } = currentPath;
        const response: any = await getReport({
          themeShortCode,
          maxResultPage: 20,
          pageNumber: 1,
          ...queryParams,
        });
        const { reports, maxPages, maxResultPage, pageNumber, sumUnreadReports } = response;

        const formattedReports = reports.map((report: any) => ({
          ...report,
          agent: `${report.authorFirstName} ${report.authorLastName}`,
          circulationDateFormat: moment(report.circulationDate).format('DD/MM/YYYY'),
          bold: !isAgent && report.userReportStatus && report.userReportStatus < ReportStatus.seen,
        }));

        setData({
          reports: formattedReports,
          maxPages,
          maxResultPage,
          pageNumber,
          sumUnreadReports,
        });
        dispatch(setThemeData(themeShortCode, { sumUnreadReports }));
        setLoading(false);
      }
    } catch (error) {
      setData({
        reports: [],
        maxPages: 0,
        maxResultPage: 0,
        pageNumber: 0,
        sumUnreadReports: 0,
      });
      setLoading(false);
      console.log('error reports', error);
    }
  }, [queryParams, currentPath, getReport, dispatch, isAgent]);

  const handleOnExport = useCallback(async () => {
    try {
      if (currentPath && currentPath.themeShortCode) {
        const { themeShortCode } = currentPath;
        const response = await exportReports({
          themeShortCode,
          maxResultPage: 20,
          pageNumber: 1,
          ...queryParams,
        });
        let link = document.createElement('a');
        var binaryData = [];
        binaryData.push(response);
        // window.URL.createObjectURL(new Blob(response, { type: 'application/zip' }));
        link.href = window.URL.createObjectURL(new Blob(binaryData as any));
        link.download = 'rapports.xlsx';
        link.click();
        //window.
      }
    } catch (error) {
      console.log('error', error);
    }
  }, [currentPath, exportReports, queryParams]);

  const handleOnDelete = useCallback(async () => {
    if (selectedReport) {
      try {
        await deleteReport(selectedReport);

        setData((data) => {
          if (data) {
            const reports = ((data && data.reports) || []).filter(
              (report) => report.reportId !== selectedReport
            );

            return {
              ...data,
              reports,
            };
          }
          return data;
        });

        handleClose();
        show({ message: 'Le rapport a été supprimé', severity: AlertTypes.success });
      } catch (err) {
        handleClose();
        show({ message: 'Suppression du rapport impossible', severity: AlertTypes.error });
      }
    }
  }, [show, deleteReport, selectedReport, AlertTypes, handleClose]);

  useEffect(() => {
    getReportByTheme();
  }, [getReportByTheme]);
  return (
    <div>
      <div className={classes.topArea}>
        {isAgent && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              localStorage.removeItem('row');
              navigateToCreateAsct();
            }}
          >
            Nouveau
          </Button>
        )}
        <span className={classes.separator}></span>
        <Button color="primary" startIcon={<ExportIcon />} onClick={handleOnExport}>
          Exporter
        </Button>
        <Button color="primary" startIcon={<FilterIcon />} onClick={toggleFiltres}>
          filtres
        </Button>
      </div>
      {showFiltres && (
        <DynamicForm
          initialValue={{ ...filterInitialValues, ...queryParams }}
          fields={filterFields}
          onChange={handleOnChange}
          onSubmit={() => { }}
        >
          {(form: any) => (
            <ThreeBtn
              primaryTitle="Rechercher"
              // isPrimarySubmitting={form.isSubmitting}
              primaryOnClick={handleOnSearchClick}
              secondaryTitle="Effacer les filtres"
              // isSecondarySubmitting={form.isSubmitting}
              secondaryOnClick={async () => {
                console.log(filterFields)
                Object.keys(filterFields).reduce(
                  (acc, key) => ({ ...acc, [key]: filterFields[key]?.type === 'text' ? form.setFieldValue(key, '') : form.setFieldValue(key, null) }),
                  {}
                );



              }}
              disabled={form.isSubmitting}
            />
          )}
        </DynamicForm>
      )}

      <div className={classes.section}>
        <DataTable
          loading={loading}
          columns={dataColumns}
          data={data?.reports || []}
          maxPages={data?.maxPages || 1}
          maxResults={data?.maxResultPage || 1}
          page={data?.pageNumber || 1}
          onPageChange={handleOnPageChange}
          onSelect={(row) => {
            handleOnClick(row);
          }}
        />
      </div>
      <AlertDialog
        title="Suppression d'un rapport"
        description=""
        open={open}
        onClose={handleClose}
        onConfirm={handleOnDelete}
      />
    </div>
  );
};

export default DataList;
